import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import './Addresses.css';

const Addresses = () => {
  const { user, checkAuth } = useAuth();
  const [addresses, setAddresses] = useState(user?.addresses || []);
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    street: '',
    city: '',
    state: '',
    pincode: '',
    isDefault: false
  });
  const [message, setMessage] = useState({ type: '', text: '' });
  const [loading, setLoading] = useState(false);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({
      ...formData,
      [e.target.name]: value
    });
  };

  const resetForm = () => {
    setFormData({
      street: '',
      city: '',
      state: '',
      pincode: '',
      isDefault: false
    });
    setIsAdding(false);
    setEditingId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      if (editingId) {
        // Update address
        await axios.put(
          `${API_URL}/api/auth/addresses/${editingId}`,
          formData,
          { withCredentials: true }
        );
        setMessage({ type: 'success', text: 'Address updated successfully!' });
      } else {
        // Add new address
        await axios.post(
          `${API_URL}/api/auth/addresses`,
          formData,
          { withCredentials: true }
        );
        setMessage({ type: 'success', text: 'Address added successfully!' });
      }
      
      // Refresh user data
      await checkAuth();
      resetForm();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Operation failed' });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (address) => {
    setFormData({
      street: address.street,
      city: address.city,
      state: address.state,
      pincode: address.pincode,
      isDefault: address.isDefault
    });
    setEditingId(address.id);
    setIsAdding(true);
  };

  const handleDelete = async (addressId) => {
    if (!window.confirm('Are you sure you want to delete this address?')) return;

    setLoading(true);
    try {
      await axios.delete(`${API_URL}/api/auth/addresses/${addressId}`, {
        withCredentials: true
      });
      setMessage({ type: 'success', text: 'Address deleted successfully!' });
      await checkAuth();
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to delete address' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="addresses-page">
      <h1>Saved Addresses</h1>
      <p className="subtitle">Manage your delivery addresses</p>

      {message.text && (
        <div className={`message ${message.type}`}>
          {message.text}
        </div>
      )}

      {!isAdding && (
        <button className="btn-add-address" onClick={() => setIsAdding(true)}>
          + Add New Address
        </button>
      )}

      {isAdding && (
        <div className="address-form-card">
          <h3>{editingId ? 'Edit Address' : 'Add New Address'}</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Street Address *</label>
              <input
                type="text"
                name="street"
                value={formData.street}
                onChange={handleChange}
                placeholder="123 Main Street"
                required
                disabled={loading}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>City *</label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  placeholder="Bangalore"
                  required
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label>State *</label>
                <input
                  type="text"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  placeholder="Karnataka"
                  required
                  disabled={loading}
                />
              </div>
            </div>

            <div className="form-group">
              <label>Pincode *</label>
              <input
                type="text"
                name="pincode"
                value={formData.pincode}
                onChange={handleChange}
                placeholder="560001"
                maxLength="6"
                required
                disabled={loading}
              />
            </div>

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  name="isDefault"
                  checked={formData.isDefault}
                  onChange={handleChange}
                  disabled={loading}
                />
                Set as default address
              </label>
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="btn-cancel"
                onClick={resetForm}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-save"
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save Address'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="addresses-list">
        {user?.addresses?.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📍</div>
            <h3>No addresses saved</h3>
            <p>Add your first delivery address to get started</p>
          </div>
        ) : (
          user?.addresses?.map((address) => (
            <div key={address.id} className="address-card">
              {address.isDefault && (
                <span className="default-badge">Default</span>
              )}
              <div className="address-content">
                <strong>{address.street}</strong>
                <p>{address.city}, {address.state}</p>
                <p>Pincode: {address.pincode}</p>
              </div>
              <div className="address-actions">
                <button
                  className="btn-edit-small"
                  onClick={() => handleEdit(address)}
                  disabled={loading}
                >
                  Edit
                </button>
                <button
                  className="btn-delete-small"
                  onClick={() => handleDelete(address.id)}
                  disabled={loading}
                >
                  Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Addresses;
